import { Plus } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useAppAbilities from '@/components/abilities';
import NotFound from '@/components/blocks/NotFound';
import PageHeader from '@/components/common/PageHeader';
import { useActiveRole } from '@/contexts/UserSession';
import { UserRole } from '@/gql/enums';
import type { ProgramListItemDataFragment } from '@/gql/graphql';
import CreateAssessmentRuns from './CreateAssessmentRuns';
import CreateProgram from './CreateProgram';
import type { ProgramListingRef } from './ProgramListing';
import ProgramListing from './ProgramListing';
import type { ProgramListingItemGroup } from './ProgramListing/utils';
import UpdateAssessmentRuns from './UpdateAssessmentRuns';

enum ViewType {
    Programs = 'programs',
    CreateAssessmentRuns = 'createAssessmentRuns',
    UpdateAssessmentRuns = 'updateAssessmentRuns',
}

const ProgramManagementPage = () => {
    const { t } = useTranslation('programManagementPage');
    const abilities = useAppAbilities();
    const activeRole = useActiveRole();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGroup, setSelectedGroup] = useState<ProgramListingItemGroup | null>(null);
    const [newProgramModalOpen, setNewProgramModalOpen] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState<ProgramListItemDataFragment | undefined>();
    const programListRef = useRef<ProgramListingRef>(null);
    const [currentView, setCurrentView] = useState<ViewType>(ViewType.Programs);

    const handleAddProgramClick = useCallback(() => {
        setNewProgramModalOpen(true);
    }, []);

    const handleProgramCreated = () => {
        setNewProgramModalOpen(false);
        if (programListRef.current) {
            programListRef.current.refetch();
        }
    };

    const handleCreateAssessmentRuns = useCallback((record: ProgramListItemDataFragment) => {
        setSelectedProgram(record);
        setCurrentView(ViewType.CreateAssessmentRuns);
    }, []);

    const handleUpdateAssessmentRuns = useCallback(
        (record: ProgramListItemDataFragment, group: ProgramListingItemGroup) => {
            setSelectedProgram(record);
            setSelectedGroup(group);
            setCurrentView(ViewType.UpdateAssessmentRuns);
        },
        []
    );

    const handleBackToPrograms = useCallback(() => {
        setSelectedProgram(undefined);
        setSelectedGroup(null);
        setCurrentView(ViewType.Programs);
        // Refresh the program list
        if (programListRef.current) {
            programListRef.current.refetch();
        }
    }, []);

    if (!abilities.canViewPrograms()) {
        return <NotFound />;
    }

    return (
        <div className="flex w-full flex-col gap-5 lg:gap-10">
            {currentView === ViewType.Programs && (
                <>
                    <PageHeader
                        title={activeRole && activeRole.role === UserRole.Trainee ? t('programList.title') : t('title')}
                        searchProps={{
                            value: searchQuery,
                            onChange: setSearchQuery,
                            placeholder: t('programList.searchPlaceholder'),
                            ariaLabel: t('programList.ariaLabel'),
                        }}
                        actionButton={{
                            label: t('programList.createButton'),
                            onClick: handleAddProgramClick,
                            show: abilities.canCreateProgram(),
                            icon: <Plus className="size-4" strokeWidth={1.5} />,
                        }}
                    />
                    <ProgramListing
                        ref={programListRef}
                        searchText={searchQuery}
                        onCreateAssessmentRuns={handleCreateAssessmentRuns}
                        onUpdateAssessmentRuns={handleUpdateAssessmentRuns}
                    />
                </>
            )}

            {currentView === ViewType.CreateAssessmentRuns && selectedProgram && (
                <CreateAssessmentRuns
                    program={selectedProgram}
                    onClose={handleBackToPrograms}
                    onSuccess={handleBackToPrograms}
                />
            )}

            {currentView === ViewType.UpdateAssessmentRuns && selectedProgram && (
                <UpdateAssessmentRuns
                    program={selectedProgram}
                    selectedGroup={selectedGroup}
                    onClose={handleBackToPrograms}
                    onSuccess={handleBackToPrograms}
                />
            )}

            <CreateProgram
                open={newProgramModalOpen}
                onClose={() => setNewProgramModalOpen(false)}
                onCreated={handleProgramCreated}
            />
        </div>
    );
};

export default ProgramManagementPage;
